import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Smartphone } from 'lucide-react';
import { Button } from './ui/button';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
    
    if (isStandalone) {
      return; // Already installed, don't show prompt
    }

    // Check if user dismissed the prompt before
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed);
      // Show again after 7 days
      if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        return;
      }
    }

    // For iOS, show after a delay
    if (isIOSDevice) {
      const timer = setTimeout(() => setShowPrompt(true), 3000);
      return () => clearTimeout(timer);
    }

    // For Android/Desktop, listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowPrompt(true), 2000);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setShowIOSInstructions(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  if (!showPrompt) return null;

  return (
    <AnimatePresence>
      {showIOSInstructions ? (
        // iOS Instructions Modal
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-50 p-4"
          onClick={handleDismiss}
        >
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="bg-zinc-900 border border-neon-lime/30 rounded-2xl p-6 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-neon-lime/20 rounded-xl flex items-center justify-center">
                  <Smartphone className="w-6 h-6 text-neon-lime" />
                </div>
                <div>
                  <h3 className="font-bold text-white text-lg">Instalar no iPhone</h3>
                  <p className="text-zinc-500 text-sm">Siga os passos abaixo</p>
                </div>
              </div>
              <button onClick={handleDismiss} className="text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-zinc-800 rounded-full flex items-center justify-center text-neon-lime font-bold text-sm flex-shrink-0">
                  1
                </div>
                <p className="text-zinc-300 text-sm pt-1">
                  Toque no ícone <span className="text-neon-lime font-bold">Compartilhar</span> (quadrado com seta para cima) na barra do Safari
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-zinc-800 rounded-full flex items-center justify-center text-neon-lime font-bold text-sm flex-shrink-0">
                  2
                </div>
                <p className="text-zinc-300 text-sm pt-1">
                  Role para baixo e toque em <span className="text-neon-lime font-bold">"Adicionar à Tela de Início"</span>
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-zinc-800 rounded-full flex items-center justify-center text-neon-lime font-bold text-sm flex-shrink-0">
                  3
                </div>
                <p className="text-zinc-300 text-sm pt-1">
                  Toque em <span className="text-neon-lime font-bold">"Adicionar"</span> no canto superior direito
                </p>
              </div>
            </div>

            <Button
              onClick={handleDismiss}
              className="w-full mt-6 bg-neon-lime text-black font-bold"
            >
              Entendi!
            </Button>
          </motion.div>
        </motion.div>
      ) : (
        // Install Banner
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 bg-zinc-900 border border-neon-lime/30 rounded-2xl p-4 shadow-2xl z-50"
        >
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 text-zinc-500 hover:text-white p-1"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-neon-lime/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Download className="w-6 h-6 text-neon-lime" />
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-white text-sm">Instalar Ferraz Team</h4>
              <p className="text-zinc-500 text-xs mt-1">
                Adicione à tela inicial para acesso rápido e usar offline
              </p>
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <Button
              onClick={handleInstall}
              className="flex-1 bg-neon-lime text-black font-bold text-sm h-9"
            >
              <Download className="w-4 h-4 mr-2" />
              Instalar
            </Button>
            <Button
              onClick={handleDismiss}
              variant="outline"
              className="border-zinc-700 text-zinc-400 text-sm h-9"
            >
              Depois
            </Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PWAInstallPrompt;
